import { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";
import { toast } from "@/services/Utils";

import { useInitialDataImplementation } from "@/hooks/implementation/firstLoad/initialDataImplementation";
import { useBusinessCenterParentBulkLoader } from "@/hooks/implementation/firstLoad/bulkLoader";
import BusinessCenterParentTable from "@/components/BusinessCenter/BusinessCenterParentTable";
import LoadInventoryToolbar from "./LoadInventoryToolbar";
import FooterButtons from "../Buttons/FooterButtons";

import "./rowsLoadStyle.scss";

const key = "initial_data_implementation";

const BusinessCenterParentTableLoad = (props) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const fileInputRef = useRef(null);

	const { data } = useInitialDataImplementation();
	const { mutate, isLoading } = useBusinessCenterParentBulkLoader();

	const [isLockedDownload, setIsLockedDownload] = useState(false);

	const addRow = () => {
		const newBusinessCenterParent = [
			...data.businessCenterParents,
			{
				hash: uuidv4(),
				name: "",
				estateParents: [],
				estates: [],
				isNew: true,
			},
		];
		queryClient.setQueryData([key], { ...data, businessCenterParents: newBusinessCenterParent });
	};

	const handleInputChange = (index, field, value) => {
		const newBusinessCenterParent = data.businessCenterParents.map((row, i) => {
			if (i === index) {
				if (field === "estateParents") {
					return {
						...row,
						[field]: value,
						estates: [],
					};
				}
				return { ...row, [field]: value };
			}
			return row;
		});

		queryClient.setQueryData([key], { ...data, businessCenterParents: newBusinessCenterParent });
	};

	const handleExportToExcel = () => {
		setIsLockedDownload(true);
		const filename = "PlantillaCentrosDeCostos.xlsx";
		const params = {};

		const payload = {
			file: {
				key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
				params: params,
				show: true,
				url: "/client/implementation/excel_business_center_parent",
				name: filename,
				type: "excel",
				inExecution: false,
				inProgress: false,
				inChecked: false,
			},
		};

		new Promise((resolve, reject) => {
			try {
				dispatch({
					type: FETCHING_ADD_DOWNLOAD,
					payload: payload,
				});

				setTimeout(() => {
					resolve();
				}, 2000);
			} catch (error) {
				reject(error);
			}
		})
			.then(() => {
				setIsLockedDownload(false);
			})
			.catch(() => {
				setIsLockedDownload(false);
			});
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const reader = new FileReader();

		reader.onload = (e) => {
			const binaryStr = e.target.result;
			const workbook = XLSX.read(binaryStr, { type: "binary" });
			const firstSheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[firstSheetName];
			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			const groupedBusinessCenters = {};
			jsonData.forEach((item) => {
				let isNew = true;
				let hash = uuidv4();
				if (item["CODIGO"] !== "" && item["CODIGO"] !== undefined) {
					hash = item["CODIGO"];
					isNew = false;
				}

				const name = item["NOMBRE CENTRO DE COSTO"] || "";

				const estateParentObj = data.estateParents.find((estateParent) => estateParent.name === item["CAMPO"]);
				let newEstateParent = {
					value: estateParentObj.id || uuidv4(),
					label: estateParentObj.name || item["CAMPO"],
				};

				let estatesInput = item["BODEGA"]
					.split(",")
					.map((estate) => estate.trim())
					.filter((estate) => estate !== "");
				let newEstates = [];
				estatesInput.forEach((estate) => {
					const estateObj = data.estates.find((estateObj) => estateObj.display_name === estate);
					if (estateObj) {
						newEstates.push({
							value: estateObj.id,
							label: estateObj.name,
						});
					}
				});

				const estateParents = newEstateParent;
				const estates = newEstates;

				groupedBusinessCenters[hash] = {
					hash,
					name,
					estateParents,
					estates,
					isNew,
				};
			});

			const updatedBusinessCenter = Object.values(groupedBusinessCenters);

			queryClient.setQueryData([key], { ...data, businessCenters: updatedBusinessCenter });
		};

		reader.readAsBinaryString(file);
		fileInputRef.current.value = "";
	};

	const removeRow = (index, isNew) => {
		if (!isNew) {
			Swal.fire({
				title: "Accion de borrado",
				html: "Esta accion tendra efecto al presionar <strong>GUARDAR</strong> o <strong>SIGUIENTE</strong>, ¿deseas proceder?",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#7fb02f",
				cancelButtonColor: "#d33",
				confirmButtonText: "Sí, eliminarlo!",
				cancelButtonText: "Cancelar",
				didOpen: () => {
					document.body.classList.remove("swal2-height-auto");
				},
			}).then((result) => {
				if (result.isConfirmed) {
					const newBusinessCenter = data.businessCenters.filter((_, i) => i !== index);
					queryClient.setQueryData([key], { ...data, businessCenters: newBusinessCenter });

					Swal.fire({
						title: "Eliminado",
						html: "El centro de costo ha sido eliminado",
						icon: "success",
						confirmButtonColor: "#7fb02f",
						confirmButtonText: "OK",
						didOpen: () => {
							document.body.classList.remove("swal2-height-auto");
						},
					});
				} else {
					toast(addToast, "Accion cancelada por el usuario");
				}
			});
		} else {
			const newBusinessCenter = data.businessCenters.filter((_, i) => i !== index);
			queryClient.setQueryData([key], { ...data, businessCenters: newBusinessCenter });
			toast(addToast, "Centro de costo eliminado");
		}
	};

	const validBusinessCenterParent = () => {
		let isValidBusinessCenter = true;

		for (let i = 0; i < data?.businessCenterParents.length; i++) {
			const businessCenterParent = data?.businessCenterParents[i];

			if (!businessCenterParent.name) {
				isValidBusinessCenter = false;
				toast(addToast, "Se requiere que el centro de costo tenga nombre y campos asociados,revisa el elemento " + (i + 1) + ".", "error");
				break;
			}

			if (businessCenterParent.estates.length === 0) {
				isValidBusinessCenter = false;
				toast(addToast, "Se requiere que el centro de costo tenga bodega asociada, revisa el elemento " + (i + 1) + ".", "error");
				break;
			}
		}

		return isValidBusinessCenter;
	};

	const saveBusinessCenterParent = ({ moveForward = false }) => {
		if (validBusinessCenterParent()) {
			const queryData = { ...queryClient.getQueryData([key]) };
			queryData.implementationStep = moveForward ? data.implementationStep + 1 : data.implementationStep;

			mutate(queryData, {
				onSuccess: (response) => {
					if (Object.prototype.hasOwnProperty.call(response, "businessCenters")) {
						toast(addToast, response.message, "success");

						queryClient.setQueryData([key], {
							...data,
							businessCenterParents: response.businessCenterParents,
							implementationStep: response.implementationStep,
						});

						if (props.formWizardRef.current?.nextTab && moveForward) {
							props.formWizardRef.current.nextTab();
						}
					}
				},
			});
		}
	};

	if (data.businessCenterParents.length === 0) {
		addRow();
	}

	return (
		<>
			<LoadInventoryToolbar
				handleExportToExcel={handleExportToExcel}
				handleFileUpload={handleFileUpload}
				isLockedDownload={isLockedDownload}
				fileInputRef={fileInputRef}
			/>
			<BusinessCenterParentTable data={data} handleInputChange={handleInputChange} addRow={addRow} removeRow={removeRow} />

			<FooterButtons wizard={props.formWizardRef} currentIndex={props.index} handleSave={saveBusinessCenterParent} isLoading={isLoading} />
		</>
	);
};

export default BusinessCenterParentTableLoad;
